import { useState, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import { activeNetworkSelector, userSelector } from '../../../redux/selectors';
import { useGetHiresV2Query, useExportHiresMutation } from '../../../services/hiresV2';
import { trackEvent } from '../../../helpers/analytics';
import useOneTimeRender from '../../../hooks/useOneTimeRender';
import { useHiresFilters } from './useHiresFilters';
import { useHiresColumns, getHiresHeader } from './useHiresColumns';
import { isDefaultFilters } from '../defaultFilters';
import { useLocalStorage } from '../../../hooks/useLocalStorage';

export const useHiresPage = () => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const user = useSelector(userSelector);
  const history = useHistory();
  const [showFilters, setShowFilters] = useState(false);
  const defaultColumnsBase = useHiresColumns();

  const [columnsBase, setColumnsBase] = useLocalStorage(
    `${activeNetwork?.id}-hires-columns-order-v1`,
    defaultColumnsBase,
  );
  const tableRef = useRef(null);
  const [exportHires, { isLoading: isDownloadingCsv }] = useExportHiresMutation();

  const {
    filters,
    setFilters,
    keyword,
    handleKeywordChange,
    filtersCount,
    queryParams: urlQueryParams,
    setQueryParams,
  } = useHiresFilters();

  const page = urlQueryParams.page || 1;

  // Convert filters to API query params
  const apiQueryParams = useMemo(() => {
    // Extract company IDs from companyName filter (array of objects with .value property)
    const companyId = (() => {
      if (!Array.isArray(filters.companyName) || filters.companyName.length === 0) return undefined;
      const ids = filters.companyName
        .map((item) => {
          const value = typeof item === 'object' && item?.value !== undefined ? item.value : item;
          return typeof value === 'string' ? parseInt(value, 10) : value;
        })
        .filter((id) => !Number.isNaN(id));
      return ids.length > 0 ? ids : undefined;
    })();

    // Flatten sources array - each source option has an array of values
    const sources = (() => {
      if (!Array.isArray(filters.sources) || filters.sources.length === 0) return undefined;
      const flatSources = filters.sources.flatMap((item) => {
        const values = item?.value || item;
        return Array.isArray(values) ? values : [values];
      });
      return flatSources.length > 0 ? flatSources : undefined;
    })();

    // Extract importer IDs from importerIds filter
    const importerIds = (() => {
      if (!Array.isArray(filters.importerIds) || filters.importerIds.length === 0) return undefined;
      const ids = filters.importerIds
        .map((item) => {
          const value = typeof item === 'object' && item?.value !== undefined ? item.value : item;
          return typeof value === 'string' ? parseInt(value, 10) : value;
        })
        .filter((id) => !Number.isNaN(id));
      return ids.length > 0 ? ids : undefined;
    })();

    // Map firstSeen filter to firstSeenDate in YYYY-MM-DD format
    // firstSeen value is number of days (e.g., 7, 30, 90, 365)
    const firstSeenDate = (() => {
      const value = filters.firstSeen;
      if (!value || value === 'any') return undefined;
      const days = typeof value === 'number' ? value : parseInt(value, 10);
      if (Number.isNaN(days)) return undefined;
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
    })();

    // Build teamInvolvement array from both teamInvolvement filter and reviewStatus mapping
    const teamInvolvement = (() => {
      const values = [];

      // Add direct teamInvolvement filter values (multi-select returns array of objects)
      if (filters.teamInvolvement && Array.isArray(filters.teamInvolvement) && filters.teamInvolvement.length > 0) {
        filters.teamInvolvement.forEach((item) => {
          const val = typeof item === 'object' && item?.value !== undefined ? item.value : item;
          values.push(val);
        });
      }

      // Map reviewStatus to teamInvolvement values
      if (filters.reviewStatus) {
        const reviewStatusValue =
          typeof filters.reviewStatus === 'object' ? filters.reviewStatus.value : filters.reviewStatus;
        if (reviewStatusValue === 'reviewed') {
          values.push('no_influence', 'influenced', 'unknown');
        } else if (reviewStatusValue === 'to_review' || reviewStatusValue === 'pending_review') {
          values.push('pending_review');
        }
      }

      return values.length > 0 ? values : undefined;
    })();

    // Extract hire date range
    const hireDateFrom = filters.hireDate?.startDate || '';
    const hireDateTo = filters.hireDate?.endDate || '';

    const params = {
      page,
      perPage: 20,
      keyword: filters.keyword || '',
      hireDateFrom,
      hireDateTo,
      sortBy: filters.sortBy || 'date_from',
      sortDirection: filters.sortDirection || 'desc',
      // Only include filters that have values
      ...(companyId && { companyId }),
      ...(sources && { sources }),
      ...(importerIds && { importerIds }),
      ...(firstSeenDate && { firstSeenDate }),
      ...(teamInvolvement && { teamInvolvement }),
    };

    return params;
  }, [filters, page]);

  const { data, isLoading, isFetching } = useGetHiresV2Query(apiQueryParams);

  const hiresCount = data?.meta?.queryTotal ?? data?.meta?.hiresTotal ?? 0;
  const columns = useMemo(() => {
    const flattenedColumns = defaultColumnsBase.reduce((acc, column) => {
      acc[column.accessor] = column;
      return acc;
    }, {});

    return columnsBase.map((column) => {
      let header = column.accessor === 'contact' ? getHiresHeader(hiresCount) : column.Header;
      if ((isLoading || (isFetching && page === 1)) && column.accessor === 'contact') {
        header = 'Loading...';
      }

      return {
        ...flattenedColumns[column.accessor],
        pinned: column.pinned,
        Header: header,
        show: column.show,
        width: column.width,
      };
    });
  }, [columnsBase, defaultColumnsBase, hiresCount, isLoading, isFetching, page]);

  const setColumns = useCallback(
    (newColumns) => {
      setColumnsBase(newColumns);
    },
    [setColumnsBase],
  );

  const { visible: showHowItWorksModal, setVisible: setShowHowItWorksModal } = useOneTimeRender({
    user,
    keySuffix: 'contacts-howitworks-modal',
  });

  // Show main skeleton (List component) only on initial load
  const showMainSkeleton = isLoading && page === 1;
  // Show render skeleton (skeleton rows in table) when filters chang
  const showRenderSkeleton = isFetching && page === 1 && !isLoading;

  // Check if filters are at default state (to differentiate between "no hires" and "no results")
  const hasActiveFilters = useMemo(() => !isDefaultFilters(filters), [filters]);

  const hasMore = useMemo(() => {
    if (!data?.items || !data?.meta) return false;
    const pageNum = apiQueryParams.page;
    const totalCount = data.meta.queryTotal ?? data.meta.hiresTotal ?? 0;
    const totalPages = Math.ceil(totalCount / apiQueryParams.perPage);
    return pageNum < totalPages;
  }, [data, apiQueryParams]);

  const onLoadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setQueryParams({
        ...urlQueryParams,
        page: page + 1,
      });
    }
  }, [hasMore, isFetching, setQueryParams, urlQueryParams, page]);

  const onSort = useCallback(
    ({ sortBy, sortDirection }) => {
      // Map column accessors to API field names
      const sortByMap = {
        hiredDate: 'date_from',
        teamInvolvement: 'team_involvement',
      };
      setFilters({
        sortBy: sortByMap[sortBy] || sortBy,
        sortDirection,
      });
    },
    [setFilters],
  );

  const initialSortBy = useMemo(() => {
    // Map API field names back to column accessors
    const sortByReverseMap = {
      date_from: 'hiredDate',
      team_involvement: 'teamInvolvement',
    };
    const sortBy = filters.sortBy || 'date_from';
    const sortDirection = filters.sortDirection || 'desc';

    return {
      id: sortByReverseMap[sortBy] || sortBy,
      desc: sortDirection === 'desc',
    };
  }, [filters.sortBy, filters.sortDirection]);

  const hiresSortOptions = useMemo(
    () => [
      { field: 'date_from', label: 'Hired date', value: 'date_from' },
      { field: 'team_involvement', label: 'Assisted by team', value: 'team_involvement' },
    ],
    [],
  );

  const currentSort = useMemo(
    () => ({
      sortBy: filters.sortBy === 'date_from' ? 'date_from' : filters.sortBy,
      sortDirection: filters.sortDirection,
    }),
    [filters.sortBy, filters.sortDirection],
  );

  const toolbarOptionChange = useCallback(
    (tool, ...args) => {
      switch (tool) {
        case 'filter':
          setShowFilters(!showFilters);
          break;
        case 'sort': {
          const [{ value, direction }] = args;
          setFilters({
            sortBy: value,
            sortDirection: direction,
          });
          break;
        }
        case 'columns':
          setColumns(...args);
          break;
        case 'export': {
          // Extract filter params for export (excluding pagination)
          const { page: _page, perPage: _perPage, ...exportParams } = apiQueryParams;

          exportHires(exportParams)
            .then(() => {
              toast("Export in progress - we'll email it to you in a few minutes.", {
                icon: 'success',
                id: 'download_hires',
              });
              trackEvent('hires:export_hires', { collection_id: activeNetwork.id });
            })
            .catch(() => {
              toast('Failed to export hires', { icon: 'danger', id: 'download_hires_error' });
            });
          break;
        }
        default:
          break;
      }
    },
    [showFilters, exportHires, activeNetwork, setFilters, setColumns, apiQueryParams],
  );

  // KeywordFilter already handles debouncing internally, so we can pass handleKeywordChange directly
  const handleKeywordChangeWrapper = useCallback(
    (value) => {
      handleKeywordChange(value);
    },
    [handleKeywordChange],
  );

  return {
    // Data
    data,
    isLoading,
    isFetching,

    // Filters
    filters,
    setFilters,
    keyword,
    handleKeywordChangeWrapper,
    filtersCount,
    urlQueryParams,

    // UI State
    showFilters,
    setShowFilters,
    columns,
    setColumns,
    showHowItWorksModal,
    setShowHowItWorksModal,

    // Loading states
    showMainSkeleton,
    showRenderSkeleton,
    hasActiveFilters,

    // Pagination
    hasMore,
    onLoadMore,
    isLoadingMore: isFetching && page > 1,

    // Table
    tableRef,
    initialSortBy,
    currentSort,
    hiresSortOptions,

    // Handlers
    toolbarOptionChange,
    onSort,

    // Other
    activeNetwork,
    defaultColumns: defaultColumnsBase,
    isDownloadingCsv,
    history,
    page,
  };
};
